import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, Upload, Copy, ImageOff } from "lucide-react";
import { api, formatApiError, imgSrc } from "@/lib/api";

export default function MediaManager() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setMedia(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media", fd);
      toast.success("Image uploaded to the media library.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(imgSrc(url));
    toast.success("Image URL copied — paste it into any product or article image field.");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image permanently?")) return;
    try {
      await api.delete(`/admin/media/${id}`);
      toast.success("Image deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="media-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-xl font-extrabold text-brand-deep">Media Library ({media.length})</h2>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-button" className="flex items-center gap-2 rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold text-white hover:bg-brand-forest transition-all disabled:opacity-60">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Image
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-deep animate-spin" /></div>
      ) : media.length === 0 ? (
        <div className="py-16 text-center text-brand-charcoal/40">
          <ImageOff className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No uploads yet. Upload product bag photos, field images or article covers here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id.slice(0, 8)}`} className="bg-white rounded-2xl border border-brand-deep/10 overflow-hidden group">
              <div className="aspect-square bg-brand-beige">
                <img src={imgSrc(m.url)} alt={m.filename} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <span className="text-[10px] text-brand-charcoal/45 truncate">{(m.size / 1024).toFixed(0)} KB</span>
                <div className="flex gap-1.5">
                  <button onClick={() => copyUrl(m.url)} data-testid="media-copy-button" className="w-8 h-8 rounded-full bg-brand-deep/5 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all" title="Copy URL">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(m.id)} data-testid="media-delete-button" className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
